import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CommonButton from "../utilities/formElements/commonButton/CommonButton";

import { subscription } from "../../services/susbscription";

import { disconnectSocket } from "../../util/socket";
import { emptyLocalStorageOnLogout } from "../../util/helper";

import { IPaymentData } from "../../Interfaces/CommonInterface";
import { ISubscription } from "../../Interfaces/SettingInterface";

import Payment from "../views/paymentModel/Payment";
import {
  paymentFormPlans,
  PREMIUM_FEATURE_LABELS,
  FREE_TIER_FEATURE_LABELS,
} from "../../../src/constants/commonConstants";
import ActionType from "../../resources/enums";
import CongratsModel from "./InfoModel";

import InfoIcon from "../../assets/images/info-icon.svg";
import TrialBanner from "../../assets/images/skydiver.svg";

export interface ITrialFinishProps {
  className?: string;
  title?: string;
  onClose?: () => void;
  children?: React.ReactNode;
  closeModel?: any;
  name?: string;
}

const TrialFinishModel: React.FC<ITrialFinishProps> = ({
  title,
  onClose,
  name,
}) => {
  const { t: translation } = useTranslation();
  const dispatch = useDispatch();
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);

  const [changePaymentDetails, setChangePaymentDetails] =
    useState<boolean>(false);
  /*
   * 🔴 THREE STATES, NOT TWO. The modal fires on
   * `trialFinished && !activated`, and BOTH of those stay true after
   * runTrialGraceSweep downgrades someone. Without this check, a customer who
   * had already been moved to Base Jump would keep being told "nothing has
   * changed yet, you have 48 hours" - the same class of lie this whole change
   * set out to remove, just pointed the other way.
   *
   * trialGraceEndsAt is null once the window has resolved, either because the
   * customer chose or because the sweep acted. So: window open -> ask for a
   * decision; window closed -> report what happened.
   */
  const acknowledgeAndClose = async () => {
    // Failure here must not trap the customer behind the modal, so the close
    // happens regardless. The worst case is the modal returning on the next
    // load, which is where they already were.
    try {
      await subscription({
        jsonprc: "2.0",
        method: "acknowledgeTrialEnd",
        params: { userId: authReducer?.authData?._id },
        id: 0,
      });
    } catch (e) {
      // no-op: see above
    }
    if (onClose) {
      onClose();
    }
  };

  const graceEndsAtRaw = authReducer?.accountRecord?.flags?.trialGraceEndsAt;
  const graceStillOpen = Boolean(
    graceEndsAtRaw && new Date(graceEndsAtRaw).getTime() > Date.now(),
  );

  const [downgradeAccountModel, setDowngradeAccountModel] =
    useState<boolean>(false);
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<ISubscription>();
  const [paymentMethod, setPaymentMethod] = useState<IPaymentData>();
  const [closed, setClosed] = useState<boolean>(true);

  useEffect(() => {
    setClosed(false);
  }, [authReducer?.isClose]);
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const userId = authReducer.authData._id;

    const data = await subscription({
      jsonprc: "2.0",
      method: "getAccountRecord",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });

    setSubscriptionDetails(data.data?.result);

    const getPaymentMethod = await subscription({
      jsonprc: "2.0",
      method: "getPaymentMethod",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });
    setPaymentMethod(getPaymentMethod?.data?.result);
  };

  return (
    <>
      <div className="modal show-modal common-modal trial-modal">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              {name === "ChangeSub" ? (
                <h4 className="modal-title">
                  Change Your Skydive Subscription
                </h4>
              ) : (
                <h4 className="modal-title">Your Trial Period Has Ended</h4>
              )}
              {name === "ChangeSub" && (
                <CommonButton
                  type="button"
                  className="btnC btn-close btn-close-white"
                  onClick={onClose}
                  data-bs-dismiss="modal"
                />
              )}
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-5">
                  <div className="trial-banner-img">
                    <img
                      src={TrialBanner}
                      alt="trial_period_img"
                      className="banner-img-set"
                    />
                    <div className="banner-desc">
                      <p>
                        {" "}
                        By upgrading you receive many additional and useful
                        features as part of your account:
                      </p>

                      <ul>
                        <li>- Many more page views</li>
                        <li>- More URLs to track</li>
                        <li>- More view selection options</li>
                        <li>- All of your visitors and their data</li>
                        {/* Was a vague "Longer data retention" - every other
                            surface that mentions retention (Settings billing
                            tab, Sidebar date filter, Playback date picker)
                            states the actual day count/cutoff, this was the
                            one holdout. Hardcoded against the real
                            subscriptionplans DB values (Base Jump storageDays
                            = 30, the VIEWING window - not the separate 90-day
                            raw-data deletion buffer, a different number an
                            earlier pass here wrongly used) since this is
                            comparing the CURRENT trial plan against what
                            upgrading grants, not the account's own already-
                            set entitlement value. Also worth noting inline:
                            paid tiers are 365 days / 1 year, not literally
                            "Unlimited" - see this project's own memory on why
                            that language needs rollups to actually be true. */}
                        <li>- 1 year data retention (vs. 30 days on Base Jump)</li>
                        {/* Playback/flow lines/interaction pulses were
                            entirely absent from this sell copy despite
                            being real, tier-gated premium features
                            (entitlements.ts), not just bigger numbers on
                            the same free-tier features. */}
                        {PREMIUM_FEATURE_LABELS.map((label) => (
                          <li key={label}>- {label}</li>
                        ))}
                      </ul>
                      <div className="mt-2">
                        <div className="tooltip-hover">
                          <u>Compare Plans</u>
                          <div className="tooltip-onimage">
                            <img src={InfoIcon} alt="info_icon" />

                            <div className="tooltip-list">
                              <ul>
                                {paymentFormPlans.map((item, key) => (
                                  <li>
                                    <p className="title">{item.title}</p>
                                    <div className="amount">
                                      <h3>{item.cost}</h3>
                                      <p> {item.billingPeriod}</p>
                                      {/* Was monthly-only, silently out of
                                          date since annual pricing shipped
                                          (2026-08-13) - Base Jump has no
                                          annualCost (free either way), so
                                          this line only appears for the
                                          two paid tiers. */}
                                      {/* Reported: "reads very disjointed" -
                                          Base Jump has no annual price, so
                                          omitting this line entirely left
                                          its title/description/entitlements
                                          sitting higher than the two paid
                                          columns, which DO have this line.
                                          Always rendering it (hidden via
                                          visibility, not display:none -
                                          that would collapse the space right
                                          back) reserves identical vertical
                                          space in all three columns, so
                                          everything below lines up across
                                          the row regardless of which plan
                                          has a real annual price. */}
                                      <p
                                        className="annual-price-line"
                                        style={
                                          item.annualCost
                                            ? undefined
                                            : { visibility: "hidden" }
                                        }
                                      >
                                        or {item.annualCost || "$0"}{" "}
                                        {item.annualBillingPeriod || "Per Year"}{" "}
                                        <span className="save-badge">
                                          Save 17%
                                        </span>
                                      </p>
                                    </div>
                                    <h5>{item.description}</h5>
                                    <div className="points-list">
                                      <p>
                                        {item.pageviews} pageviews / {"month"}
                                      </p>
                                      <p> {item.websites} website to track</p>
                                      <p>
                                        {item.dataStorage}
                                        {item.cost === "$0"
                                          ? " days data storage"
                                          : " year data storage"}
                                      </p>
                                      <p>{item.urls} site URLs to track</p>
                                      <p>
                                        {item.mainPageviews} tracked page
                                        slots
                                      </p>
                                      {/* The free plan lists what it
                                          actually includes instead of
                                          nothing - see
                                          FREE_TIER_FEATURE_LABELS. */}
                                      {!item.premiumFeaturesIncluded &&
                                        FREE_TIER_FEATURE_LABELS.map(
                                          (label) => (
                                            <p key={label}>{label}</p>
                                          )
                                        )}
                                      {item.premiumFeaturesIncluded &&
                                        PREMIUM_FEATURE_LABELS.map(
                                          (label) => (
                                            <p key={label}>{label}</p>
                                          )
                                        )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                              <div className="arrow-down" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="trial-perios-desc">
                    {name === "ChangeSub" ? (
                      <h5>Ready to make the jump?</h5>
                    ) : (
                      <h5>You did it!</h5>
                    )}
                    {name === "ChangeSub" ? (
                      <h2>Upgrade or downgrade your account</h2>
                    ) : (
                      <h2>Congrats on a successful trial dive.</h2>
                    )}

                    {name === "ChangeSub" ? (
                      <p>
                        {/* Was accountRecord.createdAt (when the account was
                            created), not flags.trialFinishDate (when the
                            trial actually ends/ended) - showed the wrong
                            date entirely, off by however long the trial had
                            already been running. */}
                        {` Your 14-day trial will end on${
                          authReducer?.accountRecord?.flags?.trialFinishDate &&
                          new Date(
                            authReducer?.accountRecord?.flags?.trialFinishDate
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        }`}{" "}
                        . We hope you enjoyed all the thrills and excitement of
                        Skydiving.
                      </p>
                    ) : (
                      <p>
                        {` Your 14-day trial came to a close on ${
                          authReducer?.accountRecord?.flags?.trialFinishDate &&
                          new Date(
                            authReducer?.accountRecord?.flags?.trialFinishDate
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        }`}{" "}
                        . We hope you enjoyed all the thrills and excitement of
                        Skydiving. We loved seeing you make the jump.
                      </p>
                    )}

                    <div>
                      {/* The ended-trial branch carries no label: "You can:"
                          read as an introduction to a list, and the copy below
                          now opens with "Nothing has changed yet", which is a
                          statement rather than an option. */}
                      {name === "ChangeSub" ? (
                        <p className="m-0">At any time you can:</p>
                      ) : null}
                      {/*
                        🔴 BRANCHED 2026-09-24. These two paragraphs were
                        UNBRANCHED, so both variants said "Your account has
                        moved to Base Jump" - false for a trial that is still
                        running (the ChangeSub variant), and false again for a
                        trial that just ended, now that expireTrial no longer
                        downgrades anything (850c81c).

                        The ended-trial copy now matches the TRIAL_EXPIRATION
                        email word for word in substance: nothing has changed
                        yet, here are the two options, here is what happens if
                        you do nothing. The modal and the email must not
                        describe different states of the same account.

                        🔴 "48 hours" must track TRIAL_GRACE_HOURS in the
                        backend (SubscriptionApi/Service/account.ts). If that
                        constant moves, this line and both email bodies move
                        with it.
                      */}
                      {name === "ChangeSub" ? (
                        <>
                          <p className="m-0">
                            Upgrade to a paid plan for more page views, longer
                            history and the full visualization.
                          </p>
                          <p className="m-0">
                            Or move to Base Jump, our forever free plan, at any
                            time.
                          </p>
                        </>
                      ) : graceStillOpen ? (
                        <>
                          <p className="m-0">
                            Nothing has changed yet. Your tracking is still
                            running and your limits are unchanged for the next
                            48 hours.
                          </p>
                          <p className="m-0">
                            Upgrade to keep your current allowances, or move to
                            Base Jump, our forever free plan, and keep tracking
                            on smaller limits.
                          </p>
                          <p className="m-0">
                            If you do not choose within 48 hours, we will move
                            you to Base Jump. Nothing is deleted either way.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="m-0">
                            Your account has moved to Base Jump, our forever
                            free plan. Your data and tracking continue on its
                            limits.
                          </p>
                          <p className="m-0">
                            Upgrade any time for more page views, longer history
                            and the full visualization.
                          </p>
                        </>
                      )}
                    </div>

                    <div className="trial-align-btn">
                      {/*
                        🔴 A REAL CHOICE AGAIN (2026-09-24).
                        History, because it has flipped twice:
                         - Originally "Downgrade to the free plan", firing a
                           real changeSubscription.
                         - 2026-08-27 (B8): reduced to a plain dismissal,
                           because expireTrial had ALREADY downgraded the
                           account, so the write was redundant and on the
                           money path.
                         - Now: expireTrial no longer downgrades. During the
                           48h window the customer is still on the plan they
                           had, so choosing the free tier is a genuine action
                           and has to actually perform it. A dismissal here
                           would leave them in the window and downgrade them
                           later by sweep anyway - i.e. the button would lie.
                      */}
                      <div>
                        <CommonButton
                          type="button"
                          label="Continue on Base Jump"
                          className="theme-btn success-outline-btn transparent text-uppercase fontBold"
                          onClick={() => {
                            /*
                             * 🔴 PER-STATE, because the same button means two
                             * different things.
                             *
                             * Window still open: the customer is on the trial
                             * plan and choosing the free tier is a real
                             * change, so it goes through the confirmation and
                             * a real changeSubscription.
                             *
                             * Window closed: runTrialGraceSweep already moved
                             * them to Base Jump. Firing changeSubscription for
                             * the plan they are already on is bug B8 exactly -
                             * a redundant write on the money path. But a plain
                             * dismissal would not set flags.activated, so the
                             * modal would return on every load forever. Hence
                             * acknowledgeTrialEnd, which sets that one flag
                             * and nothing else.
                             */
                            if (graceStillOpen) {
                              setDowngradeAccountModel(true);
                            } else {
                              acknowledgeAndClose();
                            }
                          }}
                        />
                      </div>
                      <div className="button-label">
                        <CommonButton
                          type="submit"
                          label="Upgrade Now"
                          className="theme-btn success-btn text-uppercase fontBold"
                          onClick={() => {
                            setClosed(true);
                            setChangePaymentDetails(true);
                          }}
                        />
                        {/* Was a flat "$39 a month" - no longer true since
                            annual pricing shipped (2026-08-13): High
                            Altitude billed annually works out to
                            $32.50/mo, genuinely lower than the $39 monthly
                            rate this copy was quoting as the floor. States
                            the real lowest effective rate instead of a
                            stale monthly-only number. */}
                        <p className="font12">As low as $32.50/mo, billed annually</p>
                      </div>
                    </div>
                    {name === "ChangeSub" ? (
                      ""
                    ) : (
                      <div>
                        <button
                          className="logout-btn"
                          onClick={() => {
                            dispatch({
                              type: ActionType.LOGOUT,
                              payload: "",
                            });
                            disconnectSocket();
                            emptyLocalStorageOnLogout();
                          }}
                        >
                          Logoff
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {downgradeAccountModel && (
        <CongratsModel
          title={"Downgrade Your Account"}
          onClose={() => {
            setDowngradeAccountModel(false);
          }}
          name="downgradeConfirmation"
          header={true}
        />
      )}

      {changePaymentDetails && closed && (
        <Payment
          onClose={() => {
            setChangePaymentDetails(false);
          }}
          subscriptionData={subscriptionDetails}
          paymentMethod={paymentMethod}
        />
      )}
    </>
  );
};
export default TrialFinishModel;
